import { useCallback, useEffect, useRef, useState } from 'react';

import { Button, Drawer, Icon, Popover, Spinner, toast } from '@/components/ui';
import {
  isActive,
  saveExportFile,
  useClearExport,
  useExportJobs,
  type ExportJobView,
} from '@/features/exports/api/exports.api';
import { useClickOutside, useEscapeKey } from '@/hooks';
import { cn } from '@/lib/cn';
import { useExportsStore } from '@/stores/exports.store';
import { ExportStatus } from '@/types/domain';

/**
 * The download status that lives in the topbar.
 *
 * It is the one thing watching the export poll, so it carries three jobs at
 * once: the count badge that says something is building, the tray that lists
 * every recent export with a Save on the ready ones, and the popup that offers
 * to save a job the moment it finishes. It renders nothing at all when there is
 * nothing to show, so it never sits in the bar as an empty icon.
 */
export function ExportStatusMenu() {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const { data: jobs = [] } = useExportJobs();
  const trackedIds = useExportsStore((state) => state.trackedIds);
  const justFinishedId = useExportsStore((state) => state.justFinishedId);
  const markFinished = useExportsStore((state) => state.markFinished);
  const dismissFinished = useExportsStore((state) => state.dismissFinished);

  const clearExport = useClearExport();

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  useClickOutside([triggerRef, panelRef], close, open);
  useEscapeKey(() => {
    close();
    triggerRef.current?.focus();
  }, open);

  /**
   * Notices when a watched job crosses the finish line.
   *
   * Compares each poll against the statuses it last saw, so a job that turns
   * READY or FAILED is caught once, not on every poll after. It only reacts to
   * jobs this browser started - a colleague's export on another device should
   * not throw a save dialog in front of you. No React state is set here, only a
   * toast and a store action, which is what keeps this off the forbidden
   * set-state-in-an-effect path.
   */
  const seen = useRef<Map<string, ExportStatus>>(new Map());

  useEffect(() => {
    for (const job of jobs) {
      const previous = seen.current.get(job.id);
      const mine = trackedIds.includes(job.id);

      if (previous && previous !== job.status && mine) {
        if (job.status === ExportStatus.READY) {
          toast.success('Your export is ready', `${job.datasetLabel} — ${rowLabel(job.rowCount)}`);
          markFinished(job.id);
        } else if (job.status === ExportStatus.FAILED) {
          toast.error('Export failed', job.error || 'The export could not be built');
        }
      }
    }

    /* Rebuilt from the jobs still present, so a reaped job's entry does not
       linger and the map cannot grow without bound. */
    const next = new Map<string, ExportStatus>();
    for (const job of jobs) {
      next.set(job.id, job.status);
    }
    seen.current = next;
  }, [jobs, trackedIds, markFinished]);

  const handleSave = useCallback(
    async (job: ExportJobView) => {
      try {
        await saveExportFile(job);
      } catch (error) {
        toast.error('Could not save the file', error instanceof Error ? error.message : undefined);
        return;
      }

      /* Saved, so the job has done its job - clear it from the database, which
         is the last step the whole thing was asked to do. */
      clearExport.mutate(job.id);
      dismissFinished();
    },
    [clearExport, dismissFinished],
  );

  const activeCount = jobs.filter(isActive).length;
  const savePrompt = jobs.find((job) => job.id === justFinishedId) ?? null;

  /* Nothing recorded means nothing to show - the bar stays clean. The popup can
     still be open for a job that was just cleared, so it is rendered regardless
     below. */
  if (jobs.length === 0) {
    return <SavePrompt job={savePrompt} onSave={handleSave} onDismiss={dismissFinished} />;
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={activeCount > 0 ? `Exports — ${String(activeCount)} preparing` : 'Exports'}
        title="Exports"
        onClick={() => {
          setOpen((previous) => !previous);
        }}
        className={cn(
          'relative rounded-sm p-2 text-content-muted transition-colors hover:bg-surface-muted hover:text-content sm:p-1.5',
          open && 'bg-surface-muted text-content',
        )}
      >
        {activeCount > 0 ? <Spinner size={15} /> : <Icon name="download" size={15} />}

        {activeCount > 0 && (
          <span
            aria-hidden="true"
            className="absolute -top-0.5 -right-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-content"
          >
            {activeCount}
          </span>
        )}
      </button>

      <Popover anchorRef={triggerRef} open={open} align="end" role="dialog" aria-label="Exports">
        <div ref={panelRef} className="w-80 max-w-[calc(100vw-1rem)] p-2">
          <p className="px-1 pb-1.5 text-2xs font-bold tracking-wide text-content-subtle uppercase">
            Exports
          </p>

          <ul className="space-y-0.5">
            {jobs.map((job) => (
              <li key={job.id}>
                <ExportRow
                  job={job}
                  busy={clearExport.isPending}
                  onSave={handleSave}
                  onClear={() => {
                    clearExport.mutate(job.id);
                  }}
                />
              </li>
            ))}
          </ul>
        </div>
      </Popover>

      <SavePrompt job={savePrompt} onSave={handleSave} onDismiss={dismissFinished} />
    </>
  );
}

function rowLabel(count: number): string {
  return `${String(count)} ${count === 1 ? 'row' : 'rows'}`;
}

/** One export in the tray. */
function ExportRow({
  job,
  busy,
  onSave,
  onClear,
}: {
  job: ExportJobView;
  busy: boolean;
  onSave: (job: ExportJobView) => void;
  onClear: () => void;
}) {
  const ready = job.status === ExportStatus.READY;
  const failed = job.status === ExportStatus.FAILED;

  return (
    <div className="flex items-center gap-2 rounded-sm px-1 py-1.5">
      <span className="text-content-subtle">
        {ready ? (
          <Icon name="check-circle" size={16} className="text-success" />
        ) : failed ? (
          <Icon name="alert-circle" size={16} className="text-danger" />
        ) : (
          <Spinner size={16} />
        )}
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-content">{job.datasetLabel}</p>
        <p className="truncate text-2xs text-content-subtle">
          {ready
            ? rowLabel(job.rowCount)
            : failed
              ? job.error || 'Could not be built'
              : 'Preparing…'}
        </p>
      </div>

      {ready && (
        <Button
          size="sm"
          variant="secondary"
          iconLeft="download"
          onClick={() => {
            onSave(job);
          }}
        >
          Save
        </Button>
      )}

      {(ready || failed) && (
        <button
          type="button"
          aria-label="Clear"
          title="Clear"
          disabled={busy}
          onClick={onClear}
          className="rounded-sm p-1 text-content-subtle transition-colors hover:bg-surface-muted hover:text-content disabled:opacity-50"
        >
          <Icon name="close" size={14} />
        </button>
      )}
    </div>
  );
}

/**
 * The popup that offers to save a freshly finished export.
 *
 * A small drawer rather than a bare toast, because saving a file is a decision
 * - where to put it, whether to keep it - and the person who asked for the
 * export should be met with the Save button, not have to go hunting for it.
 */
function SavePrompt({
  job,
  onSave,
  onDismiss,
}: {
  job: ExportJobView | null;
  onSave: (job: ExportJobView) => void;
  onDismiss: () => void;
}) {
  return (
    <Drawer
      open={Boolean(job)}
      onClose={onDismiss}
      title="Export ready"
      description={job?.datasetLabel}
      width="sm"
    >
      {job && (
        <div className="space-y-4 p-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 rounded-sm border border-success/25 bg-success-soft p-2 text-success">
              <Icon name="check-circle" size={18} />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-content">{job.fileName}</p>
              <p className="mt-0.5 text-xs text-content-muted">
                {rowLabel(job.rowCount)} ready to save as a spreadsheet.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="md" onClick={onDismiss}>
              Not now
            </Button>
            <Button
              variant="primary"
              size="md"
              iconLeft="download"
              onClick={() => {
                onSave(job);
              }}
            >
              Save the file
            </Button>
          </div>

          <p className="text-2xs text-content-subtle">
            Saving clears this export from the server. You can start it again any time.
          </p>
        </div>
      )}
    </Drawer>
  );
}
